/**
 * 飞书 OAuth 前端工具：回调路径、授权页安全校验、同窗兜底回跳。
 *
 * 安全边界：
 * - code/state 只在回调页提交给专用接口，不进入 URL 历史、日志与 localStorage；
 *   sessionStorage 仅有 larkCallback 的 5 分钟一次性托管（跨 SSO 整页跳转
 *   存续，消费即清——细节与取舍见 bootstrap/larkCallback.ts 头注释）；
 * - auth_url 只允许 https + 飞书授权域，防止被篡改成钓鱼地址；
 * - returnTo 只接受同源 /assistant 路径，且只保留 threadId、ui 形态参数与
 *   statusBarHeight（数字，小程序容器顶栏高度）——白名单值，避免开放重定向。
 */
import { isFeishuHost } from './document'

/** 与后端 FEISHU_OAUTH_REDIRECT_URI 配置保持一致（开放平台白名单同步） */
export const LARK_CALLBACK_PATH = '/assistant/feishu/callback'

/**
 * 授权弹窗的 window.name 标记。
 * window.name 跨域跳转后仍保留，回调页据此区分“弹窗”与“同窗兜底”，
 * 又不依赖 window.opener（打开后已主动断开）。
 */
export const LARK_AUTH_WINDOW_NAME = 'agui-lark-oauth'

const RETURN_TO_KEY = 'agui.lark.returnTo'

export type LarkAuthLaunchResult = 'popup' | 'redirect' | 'invalid'

function parseUrl(raw?: string | null): URL | null {
  if (typeof raw !== 'string' || !raw.trim()) return null
  try {
    return new URL(raw.trim(), window.location.origin)
  } catch {
    return null
  }
}

/** 授权页只接受 https + 飞书域名（含私有化部署的 *.feishu.cn / *.larksuite.com） */
export function isLarkAuthUrl(raw?: string | null): boolean {
  const url = parseUrl(raw)
  return Boolean(url && url.protocol === 'https:' && isFeishuHost(url.hostname))
}

/**
 * 规范化站内回跳地址。
 * 只允许 /assistant 页面，丢弃其余 query（threadId、ui 形态参数与
 * statusBarHeight 单独保留并重新编码；ui 仅接受 'mobile'|'desktop'、
 * statusBarHeight 仅接受数字——白名单值防任意参数注入），同时拦截反斜杠、
 * 控制字符、带凭据的绝对 URL 等常见绕过形态。
 * statusBarHeight 是小程序容器注入的顶栏高度（启动时一次性读入 CSS 变量）：
 * 同窗授权回跳若丢掉它，回页后 --status-bar-height=0、顶栏被系统状态栏
 * 遮挡（2026-10-27 评审修复）。
 */
export function safeReturnTo(raw?: string | null): string | null {
  // 字符类刻意匹配控制字符以拦截绕过形态，故豁免 no-control-regex
  // eslint-disable-next-line no-control-regex
  if (!raw || raw.length > 512 || /[\\\u0000-\u001f]/.test(raw)) return null
  const url = parseUrl(raw)
  if (!url || url.origin !== window.location.origin) return null

  const isAssistantPath = url.pathname === '/assistant' || url.pathname.startsWith('/assistant/')
  if (!isAssistantPath || url.pathname === LARK_CALLBACK_PATH) return null

  const parts: string[] = []
  const threadId = url.searchParams.get('threadId')
  if (threadId) parts.push(`threadId=${encodeURIComponent(threadId)}`)
  const ui = url.searchParams.get('ui')
  if (ui === 'mobile' || ui === 'desktop') parts.push(`ui=${ui}`)
  const statusBarHeight = url.searchParams.get('statusBarHeight')
  if (statusBarHeight && /^\d+(\.\d+)?$/.test(statusBarHeight)) {
    parts.push(`statusBarHeight=${statusBarHeight}`)
  }
  const query = parts.length ? `?${parts.join('&')}` : ''
  return `${url.pathname}${query}${url.hash}`
}

function rememberReturnTo(): void {
  const target = safeReturnTo(`${window.location.pathname}${window.location.search}${window.location.hash}`)
  try {
    sessionStorage.setItem(RETURN_TO_KEY, target ?? '/assistant')
  } catch {
    // 隐私模式禁用 storage：同窗兜底回跳到默认助手页即可
  }
}

/** 读取并清除同窗授权前的回跳地址（只消费一次） */
export function consumeReturnTo(): string | null {
  let raw: string | null = null
  try {
    raw = sessionStorage.getItem(RETURN_TO_KEY)
    sessionStorage.removeItem(RETURN_TO_KEY)
  } catch {
    return null
  }
  return safeReturnTo(raw)
}

/**
 * 打开飞书授权页：优先新窗口，弹窗被拦截时同页跳转（回调页负责带回）。
 * 返回 invalid 表示 auth_url 未通过安全校验，调用方应提示并重取链接。
 *
 * 注意：不能给 window.open 传 noopener —— 带该特性时返回值恒为 null，
 * 会误判为“弹窗被拦截”并跳走当前页。这里先开空白窗，再断开 opener，
 * 既保留窗口句柄，又满足“新页面无法反向操作宿主”的安全要求。
 */
export function launchLarkAuth(authUrl: string): LarkAuthLaunchResult {
  if (!isLarkAuthUrl(authUrl)) return 'invalid'

  const popup = window.open('', LARK_AUTH_WINDOW_NAME, 'width=520,height=680')
  if (!popup) {
    rememberReturnTo()
    window.location.assign(authUrl)
    return 'redirect'
  }

  try {
    popup.opener = null
  } catch {
    // 个别浏览器禁止改写：页面随即跳转到飞书域，实际风险窗口极短
  }
  popup.location.replace(authUrl)
  return 'popup'
}
